import React, {
  createContext,
  useCallback,
  useContext, useEffect, useMemo,
  useState,
} from 'react';

import type { PhotoCommonType, Photos, PhotoType } from 'Utilities/profiling/@types';

import { useGetPhotos, useSortedPhotos } from '../containers/app/vehicleProfiling/VehicleProfiling.hooks';

type PhotosContextTypes = {
  addPhoto: (photo: PhotoCommonType) => void;
  conditionPhotos: Photos;
  deletePhoto: (file: PhotoCommonType) => void;
  isError: any;
  isLoading: boolean;
  photos: PhotoCommonType[];
  resupplyPhotos: Photos;
  serviceHistoryPhotos: Photos;
  updatePhoto: (photo: PhotoCommonType) => void;
  vehiclePhotos: Photos;
}

const initialProps: PhotosContextTypes = {
  addPhoto: () => null,
  conditionPhotos: [],
  deletePhoto: () => null,
  isError: null,
  isLoading: false,
  photos: [],
  resupplyPhotos: [],
  serviceHistoryPhotos: [],
  updatePhoto: () => null,
  vehiclePhotos: [],
};

const PhotosContext = createContext(initialProps);

const PhotosProvider = ({ children }: { children: React.ReactNode }) => {
  const [photos, setPhotos] = useState<PhotoCommonType[]>([]);
  const { isError, isLoading, photos: allPhotos } = useGetPhotos();

  useEffect(() => {
    if (allPhotos) {
      setPhotos(allPhotos.map((photo: PhotoType) => ({ ...photo, isUploaded: true })));
    }
  }, [allPhotos]);

  const { conditionPhotos, resupplyPhotos, serviceHistoryPhotos, vehiclePhotos } = useSortedPhotos(photos as Photos);

  const addPhoto = useCallback((photo: PhotoCommonType) => {
    setPhotos((prev) => [...prev, photo]);
  }, []);

  const updatePhoto = useCallback((photo: PhotoCommonType) => {
    setPhotos((prev) => prev.map((it) => (it.id === photo.id ? photo : it)));
  }, []);

  const deletePhoto = useCallback((file: PhotoCommonType) => {
    setPhotos((prev) => prev.filter((photo) => photo.id !== file.id));
  }, []);

  const value = useMemo(() => ({
    addPhoto,
    conditionPhotos,
    deletePhoto,
    isError,
    isLoading,
    photos,
    resupplyPhotos,
    serviceHistoryPhotos,
    updatePhoto,
    vehiclePhotos,
  }), [
    addPhoto,
    conditionPhotos,
    deletePhoto,
    isError,
    isLoading,
    photos,
    serviceHistoryPhotos,
    updatePhoto,
    vehiclePhotos,
    resupplyPhotos,
  ]);

  return (
    <PhotosContext.Provider {...{ value }}>
      {children}
    </PhotosContext.Provider>
  );
};

const usePhotosContext = () => useContext(PhotosContext);

export {
  PhotosProvider, PhotosContext, usePhotosContext,
};
